const Eloquent = window.Eloquent;

/**
 * Get law firm
 * @param {Number} id
 * @returns {Promise}
 */
export function getLawFirm(id) {
    return Eloquent.LawFirm.getOne(id);
}

/**
 * Update law firm
 * @param {Number} id
 * @param {Object} data
 * @returns {Promise}
 */
export function updateLawFirm(id, data) {
    // Create a copy of the data to avoid modifying the original
    const processedData = { ...data };

    return Eloquent.LawFirm.updateOne(id, processedData);
}

export function updateLetterheadTemplate(lawFirmId, template) {
    return Eloquent.LawFirm.updateLetterheadTemplate(lawFirmId, template);
}

export function updateLetterheadOptions(lawFirmId, template, options) {
    return Eloquent.LawFirm.updateLetterheadOptions(lawFirmId, template, options);
}

export function updateLetterhead(lawFirmId, template, letterhead) {
    let processedLetterhead = null;

    switch (template) {
        case 'horizontal':
            processedLetterhead = convertLetterheadToBase64(['header', 'footer'], letterhead);
            break;
        case 'vertical':
            processedLetterhead = convertLetterheadToBase64(['left', 'right', 'header', 'footer'], letterhead);
            break;
        default:
            throw new Error(`Unknown template type: ${template}`);
    }

    return Eloquent.LawFirm.updateLetterhead(lawFirmId, template, processedLetterhead);
}

export function getNormalizedLetterhead(letterhead) {
    const defaultStructure = {
        horizontal: {
            options: {
                'margin-top': 35,
                'margin-bottom': 20,
                'margin-left': 5,
                'margin-right': 5,
                'header-spacing': 0,
                'footer-spacing': 0,
                headerCss: '.container {}',
                footerCss: '.container {}',
                contentCss: '.container {}'
            },
            header: [{ id: 1, isMain: true }, { id: 2, isMain: false }],
            footer: [{ id: 1, isMain: true }, { id: 2, isMain: false }]
        },
        vertical: {
            options: {
                'left-width': 21,
                'right-width': 42,
                'section-padding-y': 5,
                'section-padding-x': 5,
                'margin-top': 25,
                'margin-bottom': 20,
                'margin-left': 0,
                'margin-right': 0,
                'header-spacing': 5,
                'footer-spacing': 5,
                // eslint-disable-next-line quotes
                headerCss: ".section-middle {}\r\n.section-left {}\r\n.section-right {}",
                footerCss: '.container {}',
                contentCss: '.container {}'
            },
            left: [{ id: 1, isMain: true }],
            right: [{ id: 1, isMain: true }],
            header: [{ id: 1, isMain: true }, { id: 2, isMain: false }],
            footer: [{ id: 1, isMain: true }, { id: 2, isMain: false }]
        }
    };

    letterhead = letterhead ? JSON.parse(letterhead) : {};

    if (letterhead.header) {
        // Old structure detected, migrate to new structure
        defaultStructure.horizontal.header = letterhead.header;
        defaultStructure.horizontal.footer = letterhead.footer || [];

        delete letterhead.header;
        delete letterhead.footer;
    }

    const filterOptions = (source = {}, allowedKeys = []) => {
        return Object.fromEntries(
            Object.entries(source || {}).filter(([k]) => allowedKeys.includes(k))
        );
    };

    const horizontalAllowedKeys = Object.keys(defaultStructure.horizontal.options);
    const verticalAllowedKeys = Object.keys(defaultStructure.vertical.options);

    const result = {
        template: letterhead.template || 'horizontal',
        horizontal: {
            ...defaultStructure.horizontal,
            ...(letterhead.horizontal || {}),
            options: {
                ...filterOptions(
                    {
                        ...defaultStructure.horizontal.options,
                        ...((letterhead.horizontal && letterhead.horizontal.options) || {})
                    },
                    horizontalAllowedKeys
                )
            }
        },
        vertical: {
            ...defaultStructure.vertical,
            ...(letterhead.vertical || {}),
            options: {
                ...filterOptions(
                    {
                        ...defaultStructure.vertical.options,
                        ...((letterhead.vertical && letterhead.vertical.options) || {})
                    },
                    verticalAllowedKeys
                )
            }
        }
    };

    result.horizontal.options = {
        ...result.horizontal.options,
        headerCss: result.horizontal.options.headerCss || defaultStructure.horizontal.options.headerCss,
        footerCss: result.horizontal.options.footerCss || defaultStructure.horizontal.options.footerCss,
        contentCss: result.horizontal.options.contentCss || defaultStructure.horizontal.options.contentCss
    };

    result.vertical.options = {
        ...result.vertical.options,
        headerCss: result.vertical.options.headerCss || defaultStructure.vertical.options.headerCss,
        footerCss: result.vertical.options.footerCss || defaultStructure.vertical.options.footerCss,
        contentCss: result.vertical.options.contentCss || defaultStructure.vertical.options.contentCss
    };

    return result;
}

function convertLetterheadToBase64(sections, letterhead) {
    // create a deep copy to avoid mutating the original object
    letterhead = JSON.parse(JSON.stringify(letterhead));

    // Process headers and footers to convert content to base64
    sections.forEach(section => {
        if (letterhead[section] && Array.isArray(letterhead[section])) {
            letterhead[section].forEach(item => {
                if (item.content) {
                    // Convert HTML content to base64
                    // Use btoa with UTF-8 handling (via unescape/encodeURIComponent)
                    item.content = btoa(unescape(encodeURIComponent(item.content)));
                    item._isBase64 = true; // Flag to indicate base64 encoding
                }
            });
        }
    });

    // Convert back to JSON string
    return JSON.stringify(letterhead);
}
